import type { MedplumClient } from "@medplum/core";

interface FhirValuesetSearchOptions {
  signal?: AbortSignal;
}

/**
 * Medplum's `ValueSet/$expand` rejects a `filter` that holds more than this many whitespace
 * tokens: it answers `Filter value cannot contain more than 10 tokens`. Callers pass raw
 * text — what a provider typed into a code autocomplete, or a display string an LLM parser
 * extracted — so the cap is reachable by typing a sentence into a code search.
 */
const MAX_FILTER_TOKENS = 10;

/** Splits search text the same way the terminology server counts filter tokens. */
function tokenizeFilter(query: string): string[] {
  return query.trim().split(/\s+/).filter(Boolean);
}

// biome-ignore lint/complexity/noStaticOnlyClass: Intentional pattern for consistent ValueSet API
abstract class FhirValueset {
  static readonly URL: string;

  /**
   * Search for codes in the valueset
   *
   * @param medplum - The medplum client
   * @param query - The search query
   * @param options - Optional request behavior such as cancellation
   * @returns The codes in the valueset
   */
  static async search(medplum: MedplumClient, query: string, options?: FhirValuesetSearchOptions) {
    const tokens = tokenizeFilter(query);
    const isOverTokenCap = tokens.length > MAX_FILTER_TOKENS;

    // Past the cap the server rejects the whole request, so the caller loses every result
    // instead of getting a short list. `$expand` requires each token to match, which makes a
    // prefix of the tokens match a superset of what the full text would: send the prefix, then
    // drop the rows the dropped tokens exclude.
    const resources = await medplum.valueSetExpand(
      {
        url: this.URL,
        filter: isOverTokenCap ? tokens.slice(0, MAX_FILTER_TOKENS).join(" ") : query,
        count: 10,
      },
      { signal: options?.signal },
    );

    const contains = resources.expansion?.contains || [];
    if (!isOverTokenCap) return contains;

    const droppedTokens = tokens.slice(MAX_FILTER_TOKENS).map((token) => token.toLocaleLowerCase());
    return contains.filter((entry) => {
      const display = entry.display?.toLocaleLowerCase() ?? "";
      return droppedTokens.every((token) => display.includes(token));
    });
  }

  /**
   * Search for codes in the valueset
   *
   * @param medplum - The medplum client
   * @param code - The exact code to search for
   * @returns The code details from the valueset
   */
  static async getDisplayForCode(medplum: MedplumClient, code: string) {
    const resources = await medplum.valueSetExpand({
      url: this.URL,
      filter: code,
      count: 1,
    });
    const match = resources.expansion?.contains?.[0];
    return match?.display || code;
  }
}

/**
 * ICD-10-CM diagnosis codes.
 *
 * Uses the standard ICD-10-CM ValueSet for searching diagnosis codes
 * via Medplum's ValueSet/$expand operation.
 */
export class ConditionCodes extends FhirValueset {
  static readonly URL = "http://hl7.org/fhir/sid/icd-10-cm-selectable";
  static readonly SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm";

  /**
   * Plain ICD-10 (WHO), a genuinely different code system from the US ICD-10-CM above — not an
   * alias for it. CDA imports emit conditions under this system while the document parsers emit
   * ICD-10-CM, so anything matching a Condition by diagnosis has to accept both or it silently
   * misses half the records.
   *
   * Prefer this constant over the raw URI. The remaining inline spellings are in
   * `archive/cerbo/`, which is frozen and not to be edited, and in a `prepay-labs` test fixture.
   */
  static readonly WHO_SYSTEM = "http://hl7.org/fhir/sid/icd-10";
}

export class ProcedureCodes extends FhirValueset {
  static readonly URL = "http://www.cms.gov/Medicare/Coding/ICD10/vs";
}

/**
 * Vaccine codes.
 */
export class VaccineCodes extends FhirValueset {
  static readonly URL = "http://hl7.org/fhir/ValueSet/vaccine-code";
}

/**
 * RXNorm codes.
 */
export class MedicationCodes extends FhirValueset {
  static readonly URL = "http://www.nlm.nih.gov/research/umls/rxnorm/vs";
  static readonly SYSTEM = "http://www.nlm.nih.gov/research/umls/rxnorm";
}

/**
 * CPT codes.
 */
export class CptCodes extends FhirValueset {
  static readonly URL = "http://hl7.org/fhir/ValueSet/cpt-all";
}

/**
 * Other codings.
 */
export class LoincCodes extends FhirValueset {
  static readonly URL = "http://loinc.org/vs";
  static readonly SYSTEM = "http://loinc.org";
}

export class SnomedCodes extends FhirValueset {
  static readonly URL = "http://snomed.info/sct/vs";
  static readonly SYSTEM = "http://snomed.info/sct";
}

/**
 * SNOMED CT Substance codes (allergens, environmental substances, etc.)
 */
export class SubstanceCodes extends FhirValueset {
  static readonly URL = "http://hl7.org/fhir/ValueSet/substance-code";
  static readonly SYSTEM = "http://snomed.info/sct";
}

export class CdtCodes extends FhirValueset {
  static readonly URL = "http://www.ada.org/cdt/vs";
  static readonly SYSTEM = "http://www.ada.org/cdt";
}

export class HCPCSCodes extends FhirValueset {
  static readonly URL = "http://www.cms.gov/Medicare/Coding/HCPCSReleaseCodeSets/vs";
  static readonly SYSTEM = "http://www.cms.gov/Medicare/Coding/HCPCSReleaseCodeSets";
}

export class CvxCodes extends FhirValueset {
  static readonly URL = "http://hl7.org/fhir/sid/cvx/vs";
  static readonly SYSTEM = "http://hl7.org/fhir/sid/cvx";
}
